<template>
  <div class="mb-horarios__lista-horarios">
    <div
      class="mb-horario__container-labels border-b border-gray-200 pb-8 sticky flex items-stretch dark:border-gray-600"
    >
      <div class="w-20 basis-20" />
      <div class="flex flex-col items-start justify-end flex-1 pr-2">
        <span
          class="uppercase tracking-wider text-xs leading-none text-gray-600 dark:text-gray-300"
          >Salida</span
        >
        <h2 class="leading-tight">{{ estacionSalida?.name }}</h2>
      </div>
      <div class="flex flex-col items-start justify-end flex-1 pr-2">
        <span
          class="uppercase tracking-wider text-xs leading-none text-gray-600 dark:text-gray-300"
          >Llegada</span
        >
        <h2 class="leading-tight">{{ estacionLlegada?.name }}</h2>
      </div>
      <div class="basis-60 w-60" />
    </div>

    <div class="mb-horario__lista-horas">
      <Transition name="slide-open" mode="out-in">
        <div
          v-if="isLoadingHorarios || isFirstRun"
          class="flex items-center justify-center pt-24 w-full min-h-[300px]"
          role="status"
          aria-label="Cargando horarios"
        >
          <LucideLoader2 :size="36" class="w-36! h-36! animate-spin" />
        </div>
        <div
          v-else-if="!isLoadingHorarios && !listaHorarios.length"
          class="text-xl my-20 mb-horario-sinhoras"
          role="status"
          aria-live="polite"
        >
          <em>No hay horarios disponibles a esta hora :(</em>
        </div>
        <TransitionGroup
          v-else
          name="horarios-flip"
          tag="div"
          class="mb-horario__transition-list"
          mode="out-in"
        >
          <template
            v-for="(horario, horarioI) in listaHorarios"
            :key="`${horario.id}|${horario.S}-${horario.T}`"
          >
            <HorarioSingle
              :horario="horario"
              :estacion-salida="estacionSalida?.name || 'Salida'"
              :estacion-llegada="estacionLlegada?.name || 'Llegada'"
              :horarios-vuelta="horariosVuelta"
              :style="{ '--i': Math.min(horarioI, 12) }"
            />
            <!-- we show only every 20 schedules, and only the last one if theres more than 5 remainig schedules -->
            <MbAdsense
              v-if="
                Boolean(horarioI) &&
                !Boolean(horarioI % 20) &&
                numHorarios - horarioI > 5
              "
              ad-slot="1668393788"
              ad-format="fluid"
              full-width-responsive="false"
              ad-layout-key="-6m+eh+16-3z+5g"
            />
          </template>
        </TransitionGroup>
      </Transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Horario, IEstacion, IHorarios } from '~~/interfaces';
import { horaState } from '~~/stores/hora';
import { useScheduleFiltering } from '~~/composables/useScheduleFiltering';

// Import HorarioSingle type from the component
import HorarioSingle from './HorarioSingle.vue';
type HorarioSingleType = InstanceType<
  typeof HorarioSingle
>['$props']['horario'];

interface Props {
  limite?: number;
  horarios?: Horario[];
  horariosVuelta?: Map<string, Horario>;
  estacionSalida?: IEstacion;
  estacionLlegada?: IEstacion;
  fecha?: Date | string;
  isLoadingHorarios?: boolean;
  isFeriado?: boolean;
  isFirstRun?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  limite: 10,
  horarios: () => [],
  horariosVuelta: undefined,
  estacionSalida: undefined,
  estacionLlegada: undefined,
  fecha: new Date().toISOString(),
  isLoadingHorarios: false,
  isFeriado: false,
  isFirtsRun: true,
});

const horaBase = horaState();
const fechaActual = computed(() => horaBase.date);
const hora = computed(() => {
  try {
    return new Date(fechaActual.value).toLocaleTimeString('es-CL', {
      hour12: false,
      minute: '2-digit',
      hour: '2-digit',
      timeZone: 'America/Santiago',
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return '00:00';
  }
});

const { getCurrentDayType, formatTime, getRelativeTime, filterSchedules } =
  useScheduleFiltering();

const dia = computed(() => {
  try {
    return getCurrentDayType(new Date(fechaActual.value));
  } catch (error) {
    console.error('Error getting day type:', error);
    return 'Lu' as const;
  }
});

const parseValidHorarios = (
  horarios: Horario[] = [],
  limit = props.limite
): HorarioSingleType[] => {
  const validSchedules = horarios.filter((h) => {
    if (!h || typeof h.S !== 'string') return false;
    if (!Array.isArray(h.V) || h.V.length === 0) return false;
    return true;
  });

  // Filter and format schedules
  const filteredSchedules = filterSchedules(
    validSchedules,
    hora.value,
    dia.value,
    limit,
    props.isFeriado
  );

  // Map to final format with time formatting
  return filteredSchedules.map((h) => ({
    ...h,
    id: h.id || `${h.idsrv || ''}|${h.S}:${h.T}`,
    rel: getRelativeTime(h.S),
    S: formatTime(h.S),
    T: formatTime(h.T),
    V: h.V,
    ...(h.S2 && { S2: formatTime(h.S2) }),
    ...(h.T2 && { T2: formatTime(h.T2) }),
  }));
};

const listaHorarios = computed<HorarioSingleType[]>(() => {
  const newHorarios = parseValidHorarios(props.horarios, props.limite);
  return newHorarios;
});

const numHorarios = computed(() => listaHorarios.value.length);

const horariosVuelta = computed(() => {
  const horariosRetorno = Array.from(props.horariosVuelta?.values() || []);
  return parseValidHorarios(horariosRetorno, 0);
});
</script>

<style scoped>
.mb-horario__transition-list {
  position: relative;
  overflow: hidden;
  transition: height 0.2s ease-in-out;
  will-change: height;
}

.mb-horario__transition-list .horarios-flip-move {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.mb-horario__transition-list .horarios-flip-enter-active,
.mb-horario__transition-list .horarios-flip-leave-active {
  will-change: transform, opacity;
  transition:
    transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.35s ease-out;
  transition-delay: calc(var(--i) * 0.03s);
}

.mb-horario__transition-list .horarios-flip-enter-from,
.mb-horario__transition-list .horarios-flip-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.mb-horario__transition-list .horarios-flip-enter-to,
.mb-horario__transition-list .horarios-flip-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.slide-open-enter-active,
.slide-open-leave-active {
  transition: opacity 0.3s ease-in-out;
  will-change: opacity;
}

.slide-open-enter-from,
.slide-open-leave-to {
  opacity: 0;
}
</style>
